import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Cache of favorite IDs across the app to avoid refetch
let CACHE = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => cb(CACHE));
}

export function useFavorites() {
  const { user } = useAuth();
  const [ids, setIds] = useState(CACHE || []);

  useEffect(() => {
    if (!user) {
      const local = JSON.parse(localStorage.getItem("bb_favs") || "[]");
      setIds(local);
      CACHE = local;
      return;
    }
    if (CACHE) { setIds(CACHE); return; }
    api.get("/favorites/ids").then(({ data }) => {
      CACHE = data;
      setIds(data);
      notify();
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const cb = (next) => setIds(next || []);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);

  const toggle = async (propertyId) => {
    const isFav = (CACHE || []).includes(propertyId);
    const next = isFav ? (CACHE || []).filter((x) => x !== propertyId) : [...(CACHE || []), propertyId];
    CACHE = next;
    notify();
    if (user) {
      try {
        if (isFav) await api.delete(`/favorites/${propertyId}`);
        else await api.post(`/favorites/${propertyId}`);
      } catch (e) {
        CACHE = isFav ? [...next, propertyId] : next.filter((x) => x !== propertyId);
        notify();
        toast.error("Favorite save nahi hua");
      }
    } else {
      localStorage.setItem("bb_favs", JSON.stringify(next));
    }
  };

  return { ids, toggle, has: (id) => ids.includes(id) };
}

export default function FavoriteButton({ propertyId, className = "", size = 16, onLightBg = false, stopPropagation = true }) {
  const { has, toggle } = useFavorites();
  const active = has(propertyId);

  return (
    <button
      type="button"
      onClick={(e) => {
        if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
        toggle(propertyId);
      }}
      className={`group relative grid place-items-center rounded-full transition-all ${
        active ? "bg-red-50 text-red-500" : (onLightBg ? "bg-white/95 text-slate-600 hover:text-red-500" : "bg-black/40 backdrop-blur text-white hover:text-red-400")
      } ${className}`}
      aria-label={active ? "Remove from favorites" : "Add to favorites"}
      data-testid={`fav-btn-${propertyId}`}
    >
      <Heart size={size} fill={active ? "currentColor" : "none"} strokeWidth={active ? 0 : 2} />
    </button>
  );
}
